import { useEffect, useRef, useState } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useUiStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

interface ToastItemProps {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  body?: string
  duration?: number
}

const TOAST_CONFIG = {
  success: {
    icon: CheckCircle,
    borderColor: 'border-l-[#23a559]',
    iconColor: 'text-[#23a559]',
    bg: 'bg-[#2b2d31]',
  },
  error: {
    icon: XCircle,
    borderColor: 'border-l-[#f23f42]',
    iconColor: 'text-[#f23f42]',
    bg: 'bg-[#2b2d31]',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: 'border-l-[#f0b232]',
    iconColor: 'text-[#f0b232]',
    bg: 'bg-[#2b2d31]',
  },
  info: {
    icon: Info,
    borderColor: 'border-l-[#5865f2]',
    iconColor: 'text-[#5865f2]',
    bg: 'bg-[#2b2d31]',
  },
} as const

function ToastItem({ id, type, title, body, duration = 4000 }: ToastItemProps) {
  const removeToast = useUiStore((s) => s.removeToast)
  const [progress, setProgress] = useState(100)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    startTimeRef.current = Date.now()

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)

      if (remaining === 0) {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }, 50)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [duration])

  const config = TOAST_CONFIG[type]
  const Icon = config.icon

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'relative flex w-[360px] items-start gap-3 overflow-hidden rounded-lg border-l-4 px-4 py-3 shadow-2xl',
        config.bg,
        config.borderColor,
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', config.iconColor)} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-snug">{title}</p>
        {body && (
          <p className="mt-0.5 text-xs text-[#b5bac1] leading-snug">{body}</p>
        )}
      </div>
      <button
        onClick={() => removeToast(id)}
        className="mt-0.5 shrink-0 rounded p-0.5 text-[#87898f] transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5865f2]"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 h-0.5 bg-white/20 transition-none"
        style={{ width: `${progress}%` }}
        aria-hidden="true"
      />
    </div>
  )
}

export function ToastContainer() {
  const toasts = useUiStore((s) => s.toasts)

  if (toasts.length === 0) return null

  return (
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            id={toast.id}
            type={toast.type}
            title={toast.title}
            {...(toast.body !== undefined ? { body: toast.body } : {})}
            {...(toast.duration !== undefined ? { duration: toast.duration } : {})}
          />
        </div>
      ))}
    </div>
  )
}
